import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeCodeState } from '~/types';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

export type TypeTimeLineItem =
  | TypeTimeLineStartWorkData
  | TypeTimeLineCheckInData
  | TypeTimeLineCheckOutData
  | TypeTimeLineSafetyEducationCompletionData
  | TypeTimeLineEarlyEndWorkData
  | TypeTimeEndWorkData
  | TypeTimeLineOverWorkEarlyData
  | TypeTimeLineOverWorkLateData;

/** 근무 이력 > 조기 퇴근 */
export type TypeTimeLineEarlyEndWorkData = { type: { code: 'EARLY_END_WORK'; text: string } } & {
  data: {
    endWorkAt: string;
    eventAt: string;
  };
};

/** 근무 이력 > 퇴근 */
export type TypeTimeEndWorkData = { type: { code: 'END_WORK'; text: string } } & {
  data: {
    endWorkAt: string;
    eventAt: string;
  };
};

/** 근무 이력 > 조기 출근 */
export type TypeTimeLineOverWorkEarlyData = { type: { code: 'OVER_WORK_EARLY'; text: string } } & {
  data: {
    minutes: number;
    eventAt: string;
  };
};

/** 근무 이력 > 출근 */
export type TypeTimeLineStartWorkData = { type: { code: 'START_WORK'; text: string } } & {
  data: {
    startWorkAt: string;
    eventAt: string;
  };
};

/** 근무 이력 > 연장 근무 */
export type TypeTimeLineOverWorkLateData = { type: { code: 'OVER_WORK_LATE'; text: string } } & {
  data: {
    minutes: number;
    eventAt: string;
  };
};

/** 근무 이력 > 공정 체크인 */
export type TypeTimeLineCheckInData = { type: { code: 'CHECK_IN'; text: string } } & {
  data: {
    mainWorkProcess: string;
    middleWorkProcess: string;
    checkInAt: string;
    eventAt: string;
  };
};

/** 근무 이력 > 공정 체크아웃 */
export type TypeTimeLineCheckOutData = { type: { code: 'CHECK_OUT'; text: string } } & {
  data: {
    mainWorkProcess: string;
    middleWorkProcess: string;
    checkOutAt: string;
    eventAt: string;
  };
};

/** 근무 이력 > 안전교육 완료 */
export type TypeTimeLineSafetyEducationCompletionData = {
  type: { code: 'SAFETY_EDUCATION_COMPLETION'; text: string };
} & {
  data: {
    completionDate: string;
    expirationDate: string;
    eventAt: string;
  };
};

export const isStartWork = (item: TypeTimeLineItem): item is TypeTimeLineStartWorkData =>
  item.type.code === 'START_WORK';
export const isEndWork = (item: TypeTimeLineItem): item is TypeTimeEndWorkData => item.type.code === 'END_WORK';
export const isEarlyEndWork = (item: TypeTimeLineItem): item is TypeTimeLineEarlyEndWorkData =>
  item.type.code === 'EARLY_END_WORK';
export const isOverWorkEarly = (item: TypeTimeLineItem): item is TypeTimeLineOverWorkEarlyData =>
  item.type.code === 'OVER_WORK_EARLY';
export const isOverWorkLate = (item: TypeTimeLineItem): item is TypeTimeLineOverWorkLateData =>
  item.type.code === 'OVER_WORK_LATE';
export const isCheckIn = (item: TypeTimeLineItem): item is TypeTimeLineCheckInData => item.type.code === 'CHECK_IN';
export const isCheckOut = (item: TypeTimeLineItem): item is TypeTimeLineCheckOutData => item.type.code === 'CHECK_OUT';
export const isSafetyEducationCompletion = (
  item: TypeTimeLineItem
): item is TypeTimeLineSafetyEducationCompletionData => item.type.code === 'SAFETY_EDUCATION_COMPLETION';

type TypeTodayWorkTimelineResponse = {
  code: string;
  message: string;
  data: {
    /** 지각 시간 (분) */
    lateMinutes: number;
    /** 근무 유형 */
    workType: TypeCodeState;
    /** 근무 일자 */
    workday: string;
    /** 근무 상태 */
    workdayStatus: TypeCodeState;
    /** 근무 이력 */
    workdayTimeline: TypeTimeLineItem[];
  };
};

/** @description 오늘의 이력 타임라인 조회 훅 */
const useGetTodayWorkTimeline = () => {
  const queryClient = useQueryClient();

  const requestGetTodayWorkTimeline = async ({
    onSuccess,
    onError,
    showLoading = false,
    enabled = true,
  }: {
    onSuccess: (data: TypeTodayWorkTimelineResponse) => void;
    onError: (error: TypeErrorFormat) => void;
    showLoading?: boolean;
    enabled?: boolean;
  }) => {
    if (!enabled) {
      return;
    }

    const endpoint = '/workday-timeline';

    const query = queryClient.ensureQueryData<AxiosResponse<TypeTodayWorkTimelineResponse>>({
      queryKey: ['workday-timeline'],
      queryFn: () => axiosInstance.get(endpoint),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading });
  };

  return requestGetTodayWorkTimeline;
};

export default useGetTodayWorkTimeline;
